// lib
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { RouteComponentProps, useHistory } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import LayoutNoMap from 'src/layout/layout_no_map'

// constants & interfaces
import { NOTIFICATIONS } from 'src/constants/feed'
import { TWO_COLUMNS } from 'src/constants/layout'
import { NOTIFICATIONS_TITLE } from 'src/constants/meta_tags'
import { textResources } from 'src/lang/de'

// helpers
import { NotificationsQuery, useGlobalPermissions, useMarkAllNotificationsAsSeenMutation, useMarkNotificationsAsReadMutation, useNotifications } from '@lopo-tech/lopo-api'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/actions/app-state/app-state'
import { callToAction } from 'src/actions/onboarding_cta/onboarding_cta'
import LoadingSpinnerWrapper from 'src/components/loading_spinner/loading_spinner_wrapper'
import Notifications from 'src/components/notifications/notifications'
import { URLContext } from 'src/containers/url_provider/url_provider'
import { usePageTracking } from 'src/pages/hooks/hooks'
import { useSelectUser } from 'src/reducers/user/user_hooks'

const PER_PAGE = 10
type Notification = NotificationsQuery['notifications']['items'][number];

const NotificationsPage: React.FC<RouteComponentProps> = () => {
  const user = useSelectUser()
  usePageTracking({ user })

  const dispatch = useDispatch<AppDispatch>()

  const { canShowNotifications } = useGlobalPermissions()

  const history = useHistory()
  const { getNewsfeedRoute, getResourceLinkRoute } = useContext(URLContext)
  const newsfeedRoute = getNewsfeedRoute()

  const [markAllNotificationsAsSeen] = useMarkAllNotificationsAsSeenMutation()
  const [markNotificationsAsRead] = useMarkNotificationsAsReadMutation()

  useEffect(() => {
    // do we have permissions to read; if not, reroute to Newsfeed and show CTA
    if (!canShowNotifications) {
      dispatch(callToAction())
      history.replace(newsfeedRoute)
    }
  }, [user, canShowNotifications, dispatch, history, newsfeedRoute])

  useEffect(() => {
    if (canShowNotifications) {
      markAllNotificationsAsSeen({ variables: { input: {} } })
    }
  }, [canShowNotifications, markAllNotificationsAsSeen])

  const { data: notificationsData, loading: isLoading, fetchMore } = useNotifications({
    variables: {
      page: 1,
      perPage: PER_PAGE
    },
    skip: !canShowNotifications
  })

  const notifications = useMemo(
    () => notificationsData?.notifications.items ?? [],
    [notificationsData?.notifications.items]
  )

  const pageInfo = notificationsData?.notifications.pageInfo

  const fetchNextPage = useCallback(() => {
    const nextPage = pageInfo?.nextPage

    if (typeof nextPage !== 'number' || isLoading) {
      return
    }

    fetchMore({
      variables: {
        page: nextPage,
        perPage: PER_PAGE
      }
    })
  }, [pageInfo?.nextPage, fetchMore])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readAt) {
      markNotificationsAsRead({ variables: { input: { ids: [notification.id] } } })
    }

    if (notification.resourceLink) {
      history.push(getResourceLinkRoute(notification.resourceLink))
    }
  }

  return (
    <>
      <MetaTags
        title={NOTIFICATIONS_TITLE}
      />
      <LayoutNoMap
        activeNavigationItem={NOTIFICATIONS}
        contentWidth={TWO_COLUMNS}
        pageTitle={<StandardPageTitle text={textResources.pageTitles.notifications} />}
      >
        <LoadingSpinnerWrapper isLoading={isLoading}>
          <Notifications
            fetchMoreNotifications={fetchNextPage}
            notifications={notifications}
            onNotificationClick={handleNotificationClick}
          />
        </LoadingSpinnerWrapper>
      </LayoutNoMap>
    </>
  )
}

export default NotificationsPage
